import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import type { Tenant, TenantSettings } from '../lib/supabase';
import type { User } from '@supabase/supabase-js';
import type { DebugLog } from '../components/AuthDebugPanel';

// BUILD MODE DETECTION
const isBuildMode = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const hasBuildParam = urlParams.get('build') === '1';
  const hasEnvFlag = import.meta.env.DEV && import.meta.env.VITE_DEV_BUILD_MODE === 'true';
  return hasBuildParam || hasEnvFlag;
};

// MOCK DATA FOR BUILD MODE
const createMockTenant = (): Tenant => ({
  id: 'BUILD_TENANT_ID',
  user_id: 'BUILD_USER_ID',
  company_name: 'Build Mode Company',
  email: 'dev@build-mode.local',
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString()
});

const createMockSettings = (): TenantSettings => ({
  id: 'BUILD_SETTINGS_ID',
  tenant_id: 'BUILD_TENANT_ID',
  target_groups: ['Ärzte', 'Zahnärzte'],
  regions: ['Berlin', 'Hamburg'],
  product_categories: ['Medizintechnik'],
  email_enabled: true,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString()
});

const createMockUser = (email: string = 'dev@build-mode.local'): User => ({
  id: 'BUILD_USER_ID',
  email,
  app_metadata: {},
  user_metadata: {},
  aud: 'authenticated',
  created_at: new Date().toISOString()
} as User);

export interface DiagnosticStep {
  step: string;
  status: 'PASS' | 'FAIL' | 'PENDING';
  timestamp: string;
  details?: any;
  error?: {
    status?: number;
    code?: string;
    message?: string;
    details?: string;
    hint?: string;
  };
}

export interface DiagnosticReport {
  timestamp: string;
  basename: string;
  currentRoute: string;
  expectedRoute: string;
  steps: DiagnosticStep[];
  summary: {
    total: number;
    passed: number;
    failed: number;
  };
}

interface TenantContextType {
  user: User | null;
  tenant: Tenant | null;
  tenantSettings: TenantSettings | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean; // NEW: Simple auth flag
  authState: 'none' | 'checking' | 'signed_in';
  tenantState: 'idle' | 'bootstrapping' | 'ready' | 'error';
  loadTenantData: () => Promise<void>; // NEW: Separate data loading
  updateTenantSettings: (updates: Partial<TenantSettings>) => Promise<void>;
  signOut: () => Promise<void>;
  debugLogs: DebugLog[];
  addDebugLog: (type: DebugLog['type'], category: DebugLog['category'], message: string, details?: any) => void;
  runDiagnostic: () => Promise<void>;
  buildDiagnosticReport: () => Promise<DiagnosticReport>;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

// Helper: Retry mit Delays
async function retryWithDelay<T>(
  fn: () => Promise<T>,
  retries: number = 3,
  delays: number[] = [400, 800, 1200],
  onRetry?: (attempt: number) => void
): Promise<T | null> {
  for (let i = 0; i <= retries; i++) {
    try {
      const result = await fn();
      if (result) return result;
    } catch (err) {
      console.error(`Retry ${i + 1} failed:`, err);
      if (onRetry) onRetry(i + 1);
    }
    
    if (i < delays.length) {
      await new Promise(resolve => setTimeout(resolve, delays[i]));
    }
  }
  return null;
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [tenantSettings, setTenantSettings] = useState<TenantSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [debugLogs, setDebugLogs] = useState<DebugLog[]>([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  const [authState, setAuthState] = useState<'none' | 'checking' | 'signed_in'>('none');
  const [tenantState, setTenantState] = useState<'idle' | 'bootstrapping' | 'ready' | 'error'>('idle');

  const addDebugLog = (
    type: DebugLog['type'],
    category: DebugLog['category'],
    message: string,
    details?: any
  ) => {
    const timestamp = new Date().toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3
    });

    const log: DebugLog = { timestamp, type, category, message, details };
    setDebugLogs(prev => [...prev, log]);
    
    const emoji = type === 'error' ? '❌' : type === 'success' ? '✅' : type === 'warning' ? '⚠️' : '🔵';
    console.log(`${emoji} [${category.toUpperCase()}] ${message}`, details || '');
  };

  const buildDiagnosticReport = async (): Promise<DiagnosticReport> => {
    const base =
      (typeof __BASE_PATH__ !== "undefined" && __BASE_PATH__) ||
      ((typeof import.meta !== "undefined" && import.meta.env && import.meta.env.BASE_URL) ? import.meta.env.BASE_URL : "/");

    const report: DiagnosticReport = {
      timestamp: new Date().toISOString(),
      basename: base,
      currentRoute: window.location.pathname,
      expectedRoute: '/app',
      steps: [],
      summary: {
        total: 0,
        passed: 0,
        failed: 0
      }
    };

    // STEP 1: Session Check
    const step1: DiagnosticStep = {
      step: '1. Session Check (getSession)',
      status: 'PENDING',
      timestamp: new Date().toISOString()
    };

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        step1.status = 'FAIL';
        step1.error = {
          message: sessionError.message,
          status: sessionError.status,
          code: sessionError.name
        };
        step1.details = { error: sessionError };
      } else if (!session) {
        step1.status = 'FAIL';
        step1.error = { message: 'No session found - user not logged in' };
      } else {
        step1.status = 'PASS';
        step1.details = {
          user_id: session.user.id,
          email: session.user.email,
          access_token_length: session.access_token.length
        };
      }
    } catch (err: any) {
      step1.status = 'FAIL';
      step1.error = { message: err.message };
    }

    report.steps.push(step1);

    // STEP 2: Edge Function Invoke
    const step2: DiagnosticStep = {
      step: '2. Edge Function Invoke (bootstrap-tenant)',
      status: 'PENDING',
      timestamp: new Date().toISOString()
    };

    let tenantId: string | null = null;

    if (step1.status === 'PASS') {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const functionName = 'bootstrap-tenant';
        
        step2.details = { function_name: functionName };

        const { data: edgeData, error: edgeError } = await supabase.functions.invoke(functionName, {
          headers: {
            Authorization: `Bearer ${session!.access_token}`
          }
        });

        if (edgeError) {
          step2.status = 'FAIL';
          
          if (edgeError.message?.includes('404') || edgeError.message?.includes('not found')) {
            step2.error = {
              status: 404,
              message: 'FUNCTION NOT FOUND (name mismatch or not deployed)',
              details: edgeError.message
            };
          } else if (edgeError.message?.includes('401') || edgeError.message?.includes('403')) {
            step2.error = {
              status: edgeError.message.includes('401') ? 401 : 403,
              message: 'JWT/auth issue',
              details: edgeError.message
            };
          } else if (edgeError.message?.includes('500')) {
            step2.error = {
              status: 500,
              message: 'Edge function crashed; check Supabase logs',
              details: edgeError.message
            };
          } else {
            step2.error = {
              message: edgeError.message,
              details: JSON.stringify(edgeError)
            };
          }
        } else if (!edgeData?.tenant_id) {
          step2.status = 'FAIL';
          step2.error = {
            message: 'No tenant_id in response',
            details: JSON.stringify(edgeData)
          };
        } else {
          step2.status = 'PASS';
          tenantId = edgeData.tenant_id;
          step2.details = {
            ...step2.details,
            tenant_id: tenantId,
            response: edgeData
          };
        }
      } catch (err: any) {
        step2.status = 'FAIL';
        step2.error = { message: err.message };
      }
    } else {
      step2.status = 'FAIL';
      step2.error = { message: 'Skipped - previous step failed' };
    }

    report.steps.push(step2);

    // STEP 3: Tenant Select
    const step3: DiagnosticStep = {
      step: '3. Tenant Select (tenants table)',
      status: 'PENDING',
      timestamp: new Date().toISOString()
    };

    if (step2.status === 'PASS' && tenantId) {
      try {
        const { data: tenantData, error: tenantError, count } = await supabase
          .from('tenants')
          .select('*', { count: 'exact' })
          .eq('id', tenantId)
          .maybeSingle();

        if (tenantError) {
          step3.status = 'FAIL';
          step3.error = {
            code: tenantError.code,
            message: tenantError.message,
            details: tenantError.details,
            hint: tenantError.hint
          };
        } else if (!tenantData) {
          step3.status = 'FAIL';
          step3.error = {
            message: 'Tenant not found',
            details: `Count: ${count}`
          };
        } else {
          step3.status = 'PASS';
          step3.details = {
            tenant_id: tenantData.id,
            user_id: tenantData.user_id,
            count
          };
        }
      } catch (err: any) {
        step3.status = 'FAIL';
        step3.error = { message: err.message };
      }
    } else {
      step3.status = 'FAIL';
      step3.error = { message: 'Skipped - previous step failed' };
    }

    report.steps.push(step3);

    // STEP 4: Tenant Settings Select
    const step4: DiagnosticStep = {
      step: '4. Tenant Settings Select (tenant_settings table)',
      status: 'PENDING',
      timestamp: new Date().toISOString()
    };

    if (step3.status === 'PASS' && tenantId) {
      try {
        const { data: settingsData, error: settingsError, count } = await supabase
          .from('tenant_settings')
          .select('*', { count: 'exact' })
          .eq('tenant_id', tenantId)
          .maybeSingle();

        if (settingsError) {
          step4.status = 'FAIL';
          step4.error = {
            code: settingsError.code,
            message: settingsError.message,
            details: settingsError.details,
            hint: settingsError.hint
          };
        } else if (!settingsData) {
          step4.status = 'FAIL';
          step4.error = {
            message: 'Settings not found',
            details: `Count: ${count}`
          };
        } else {
          step4.status = 'PASS';
          step4.details = {
            settings_id: settingsData.id,
            tenant_id: settingsData.tenant_id,
            count
          };
        }
      } catch (err: any) {
        step4.status = 'FAIL';
        step4.error = { message: err.message };
      }
    } else {
      step4.status = 'FAIL';
      step4.error = { message: 'Skipped - previous step failed' };
    }

    report.steps.push(step4);

    // STEP 5: Routing Check
    const step5: DiagnosticStep = {
      step: '5. Routing Check',
      status: 'PENDING',
      timestamp: new Date().toISOString()
    };

    const isOnExpectedRoute = window.location.pathname.includes('/app');
    step5.status = isOnExpectedRoute ? 'PASS' : 'FAIL';
    step5.details = {
      current_route: window.location.pathname,
      expected_route: '/app',
      basename: base,
      match: isOnExpectedRoute
    };

    if (!isOnExpectedRoute) {
      step5.error = {
        message: 'Not on expected route',
        details: `Current: ${window.location.pathname}, Expected: /app`
      };
    }

    report.steps.push(step5);

    // Calculate summary
    report.summary.total = report.steps.length;
    report.summary.passed = report.steps.filter(s => s.status === 'PASS').length;
    report.summary.failed = report.steps.filter(s => s.status === 'FAIL').length;

    return report;
  };

  // NEW: Separate function to load tenant data (runs in background)
  const loadTenantData = async () => {
    // BUILD MODE: Use mock data
    if (isBuildMode()) {
      addDebugLog('warning', 'auth', '🔨 BUILD MODE - Using mock data');
      setTenant(createMockTenant());
      setTenantSettings(createMockSettings());
      setTenantState('ready');
      setLoading(false);
      return;
    }

    // Don't load if already loading or ready
    if (tenantState === 'bootstrapping' || tenantState === 'ready') {
      addDebugLog('info', 'auth', '⚠️ Data already loading or loaded - skipping');
      return;
    }

    try {
      setTenantState('bootstrapping');
      setLoading(true);
      setError(null);

      addDebugLog('info', 'auth', '🚀 Loading tenant data...');

      // Get current session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session?.user) {
        addDebugLog('error', 'auth', '❌ No session found');
        setTenantState('error');
        setLoading(false);
        return;
      }

      // Call bootstrap edge function to create/get tenant
      addDebugLog('info', 'edge_function', 'Calling bootstrap-tenant...');
      
      const { data: bootstrapData, error: bootstrapError } = await supabase.functions.invoke('bootstrap-tenant', {
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (bootstrapError || !bootstrapData?.tenant_id) {
        const errorMsg = 'Failed to load profile data';
        addDebugLog('error', 'edge_function', `❌ ${errorMsg}`, bootstrapError);
        setError(errorMsg);
        setTenantState('error');
        setLoading(false);
        return;
      }

      addDebugLog('success', 'edge_function', '✅ Tenant ID received', { tenant_id: bootstrapData.tenant_id });

      // Load tenant data
      const { data: tenantData, error: tenantError } = await supabase
        .from('tenants')
        .select('*')
        .eq('id', bootstrapData.tenant_id)
        .maybeSingle();

      if (tenantError || !tenantData) {
        addDebugLog('error', 'database', '❌ Failed to load tenant', tenantError);
        setError('Failed to load profile');
        setTenantState('error');
        setLoading(false);
        return;
      }

      setTenant(tenantData);
      addDebugLog('success', 'database', '✅ Tenant loaded');

      // Load tenant settings
      const { data: settingsData, error: settingsError } = await supabase
        .from('tenant_settings')
        .select('*')
        .eq('tenant_id', tenantData.id)
        .maybeSingle();

      if (settingsError || !settingsData) {
        addDebugLog('error', 'database', '❌ Failed to load settings', settingsError);
        setError('Failed to load settings');
        setTenantState('error');
        setLoading(false);
        return;
      }

      setTenantSettings(settingsData);
      addDebugLog('success', 'database', '✅ Settings loaded');

      setTenantState('ready');
      setLoading(false);
      addDebugLog('success', 'auth', '🎉 Tenant data loaded successfully!');
      
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to load data';
      addDebugLog('error', 'auth', `❌ Error: ${errorMsg}`, err);
      setError(errorMsg);
      setTenantState('error');
      setLoading(false);
    }
  };

  const runDiagnostic = async () => {
    addDebugLog('info', 'auth', '🔍 DIAGNOSTIC START - Vollständige Systemprüfung');

    const report = await buildDiagnosticReport();

    report.steps.forEach(step => {
      const type = step.status === 'PASS' ? 'success' : 'error';
      addDebugLog(type, 'auth', `${step.step}: ${step.status}`, step.details || step.error);
    });

    addDebugLog('success', 'auth', `✅ DIAGNOSTIC COMPLETE - ${report.summary.passed}/${report.summary.total} PASSED`);
  };

  const updateTenantSettings = async (updates: Partial<TenantSettings>) => {
    // BUILD MODE: Only update local state, no DB write
    if (isBuildMode()) {
      addDebugLog('warning', 'database', '🔨 BUILD MODE - Settings-Update nur lokal (kein DB-Write)');
      if (tenantSettings) {
        setTenantSettings({ ...tenantSettings, ...updates });
      }
      return;
    }

    if (!tenant || !tenantSettings) {
      throw new Error('Kein Tenant geladen');
    }

    const { data, error } = await supabase
      .from('tenant_settings')
      .update(updates)
      .eq('id', tenantSettings.id)
      .select()
      .single();

    if (error) {
      console.error('Settings update error:', error);
      throw new Error('Einstellungen konnten nicht gespeichert werden');
    }

    setTenantSettings(data);
  };

  const signOut = async () => {
    // BUILD MODE: Just clear state
    if (isBuildMode()) {
      setUser(null);
      setTenant(null);
      setTenantSettings(null);
      setIsAuthenticated(false);
      setAuthState('none');
      setTenantState('idle');
      return;
    }

    await supabase.auth.signOut();
    setUser(null);
    setTenant(null);
    setTenantSettings(null);
    setIsAuthenticated(false);
    setAuthState('none');
    setTenantState('idle');
  };

  // Initial auth check on mount
  useEffect(() => {
    // BUILD MODE: Immediate setup
    if (isBuildMode()) {
      addDebugLog('info', 'auth', '🔨 BUILD MODE - Using mock data');
      setUser(createMockUser('dev@build-mode.local'));
      setIsAuthenticated(true);
      setAuthState('signed_in');
      setLoading(false);
      return;
    }

    const checkAuth = async () => {
      try {
        setAuthState('checking');
        addDebugLog('info', 'auth', '🔍 Checking authentication...');

        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          addDebugLog('error', 'auth', '❌ Session error', sessionError);
          setAuthState('none');
          setIsAuthenticated(false);
          setLoading(false);
          return;
        }
        
        if (!session?.user) {
          addDebugLog('info', 'auth', 'ℹ️ No session - user not authenticated');
          setAuthState('none');
          setIsAuthenticated(false);
          setLoading(false);
          return;
        }

        // User is authenticated!
        addDebugLog('success', 'auth', '✅ User authenticated', {
          user_id: session.user.id,
          email: session.user.email
        });
        
        setUser(session.user);
        setIsAuthenticated(true);
        setAuthState('signed_in');
        setLoading(false);
        
      } catch (err: any) {
        addDebugLog('error', 'auth', '❌ Auth check failed', err);
        setAuthState('none');
        setIsAuthenticated(false);
        setLoading(false);
      }
    };

    checkAuth();

    // ✅ CRITICAL: Auth State Listener - Syncs auth across tabs
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      addDebugLog('info', 'auth', `🔔 Auth State Change: ${event}`, {
        event,
        user_id: session?.user?.id,
        email: session?.user?.email
      });
      
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        if (session?.user) {
          setUser(session.user);
          setIsAuthenticated(true);
          setAuthState('signed_in');
          setLoading(false);
          addDebugLog('success', 'auth', '✅ User signed in');
        }
      } else if (event === 'SIGNED_OUT') {
        addDebugLog('info', 'auth', '👋 User signed out');
        setUser(null);
        setTenant(null);
        setTenantSettings(null);
        setIsAuthenticated(false);
        setAuthState('none');
        setTenantState('idle');
        setLoading(false);
      } else if (event === 'USER_UPDATED') {
        // ✅ NEW: Handle user updates
        if (session?.user) {
          setUser(session.user);
          setIsAuthenticated(true);
          setAuthState('signed_in');
          addDebugLog('success', 'auth', '✅ User updated');
        }
      }
    });

    // ✅ NEW: Listen for storage events (cross-tab sync)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'supabase.auth.token') {
        addDebugLog('info', 'auth', '🔄 Auth state changed in another tab - reloading...');
        // Reload auth state when it changes in another tab
        checkAuth();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []); // Only run once on mount

  return (
    <TenantContext.Provider
      value={{
        user,
        tenant,
        tenantSettings,
        loading,
        error,
        isAuthenticated,
        authState,
        tenantState,
        loadTenantData,
        updateTenantSettings,
        signOut,
        debugLogs,
        addDebugLog,
        runDiagnostic,
        buildDiagnosticReport
      }}
    >
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant muss innerhalb von TenantProvider verwendet werden');
  }
  return context;
}
